// Ambulation Zone — Pinboard
// A brick-wall / corkboard found-object surface. Several artportfolio
// pieces and a taped notepaper are pinned with red/green/yellow pushpins
// at fixed seeded rotations. Each pinned item is independently draggable
// and jumps to front on drag via the jQuery .above z-index trick.

import * as React from "react";
import "./pinboard.css";
import Draggable from "react-draggable";
import $ from "jquery";

// NO REPEATS rule: every pinned photo is unique across BOTH evidence walls
// (Pinboard + RedString combined). These five — deepHeavy, embryo,
// holeHandDream, twindleGreen, rasengan — are pinned only here; RedString uses a
// fully disjoint set (skeyeSkape, cracked, lookatem, waitingRoom), so no single
// artportfolio image is pinned on both walls. -web variants are preferred where
// they exist to dodge oversized textures.
import deepHeavy from "../../../images/artportfolio/deepHeavy-web.png";
import embryo from "../../../images/artportfolio/embryo.png";
import holeHandDream from "../../../images/artportfolio/holeHandDream-web.png";
import twindleGreen from "../../../images/artportfolio/twindleGreen-web.png";
import rasengan from "../../../images/artportfolio/rasengan-web.png";

import redtack from "../../../images/redtack.png";
import greentack from "../../../images/greentack.png";
import yellowtack from "../../../images/yellowtack.png";

import notepaper from "../../../images/notepapertaped.png";

// FIXED seeded rotations (literal degrees) so positions are stable across
// renders — never Math.random() at render time.
const PINS = [
    { img: deepHeavy, tack: redtack, rot: -6, label: "deep heavy" },
    { img: embryo, tack: greentack, rot: 4, label: "embryo" },
    { img: holeHandDream, tack: yellowtack, rot: -3, label: "hole hand dream" },
    { img: twindleGreen, tack: greentack, rot: 7, label: "twindle green" },
    { img: rasengan, tack: redtack, rot: -5, label: "rasengan" },
];

// If a single pinned image ever fails to load, hide just THAT <img> (and tag
// its card) so the rest of the board still renders — a broken asset must never
// leave the whole board blank. Marking the card lets CSS drop the now-empty
// shadow/well so a missing piece degrades to a clean gap, not a black box.
function handleArtError(e) {
    const img = e.currentTarget;
    img.style.display = "none";
    const card = img.closest(".pin-card");
    if (card) card.classList.add("pin-missing");
}

function handleTackError(e) {
    // A missing pushpin is purely decorative — just hide it.
    e.currentTarget.style.display = "none";
}

function PinnedItem(props) {
    const id = "pin-" + props.index;

    function handleDrag() {
        $("#" + id).addClass("above");
    }

    return (
        <Draggable
            onDrag={handleDrag}
            handle=".pin-card"
            defaultPosition={{ x: 0, y: 0 }}
            scale={1}
        >
            <div id={id} className="pin-card">
                <div className="pin-rot" style={{ transform: "rotate(" + props.rot + "deg)" }}>
                    {/* No loading="lazy": these are the primary content of the
                        board and must paint immediately, never risk staying blank. */}
                    <img className="pin-art" src={props.img} alt={props.label} draggable={false} decoding="async" onError={handleArtError} />
                    <img className="pin-tack" src={props.tack} alt="" draggable={false} decoding="async" onError={handleTackError} />
                </div>
            </div>
        </Draggable>
    );
}

function NoteItem(props) {
    const id = "pin-note";

    function handleDrag() {
        $("#" + id).addClass("above");
    }

    return (
        <Draggable
            onDrag={handleDrag}
            handle=".pin-card"
            defaultPosition={{ x: 0, y: 0 }}
            scale={1}
        >
            <div id={id} className="pin-card">
                <div className="pin-rot" style={{ transform: "rotate(3deg)" }}>
                    <img className="pin-note" src={notepaper} alt="taped note" draggable={false} decoding="async" onError={handleArtError} />
                </div>
            </div>
        </Draggable>
    );
}

function Pinboard(props) {
    const surfaceRef = React.useRef(null);

    // TOUCH PATH for the brick "brighten on hover" effect. :hover never fires on
    // a touch screen, so a tap/press on the brick toggles a .brick-lit class that
    // drives the exact same saturate/brighten as :hover::before, then settles on
    // release. Pointer events cover mouse + touch + pen uniformly; we keep the CSS
    // :hover for mouse users so desktop behaviour is unchanged.
    const light = React.useCallback(() => {
        const el = surfaceRef.current;
        if (el) el.classList.add("brick-lit");
    }, []);
    const settle = React.useCallback(() => {
        const el = surfaceRef.current;
        if (el) el.classList.remove("brick-lit");
    }, []);

    return (
        <div
            className="pinboard-surface"
            ref={surfaceRef}
            onPointerDown={light}
            onPointerUp={settle}
            onPointerCancel={settle}
            onPointerLeave={settle}
        >
            {PINS.map((pin, i) => (
                <PinnedItem key={i} index={i} img={pin.img} tack={pin.tack} rot={pin.rot} label={pin.label} />
            ))}
            <NoteItem />
        </div>
    );
}

export default Pinboard;
